import { REVIEW_SOURCE_DEFINITIONS } from '@flexbyt/shared';
import { SourceGlyph } from '@flexbyt/widget-preview';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { IndustryScene } from '../../components/mascot/scenes.js';
import { PageHero } from '../../components/ui/hero.js';
import { ButtonAnchor, ButtonLink, SiteLink } from '../../components/ui/button.js';
import { Measure, Section } from '../../components/ui/layout.js';
import { Eyebrow, Heading, MarkPhrase, Text } from '../../components/ui/type.js';
import { WidgetShowcase } from '../../components/WidgetShowcase.js';
import { industryById, INDUSTRIES } from '../../content/industries.js';
import { sourcePagePath } from '../../content/integrations.js';
import { sampleSetForSource } from '../../content/sample-sets.js';
import { widgetContentByType, widgetPath } from '../../content/widgets.js';
import { track } from '../../lib/analytics.js';
import { breadcrumbSchema, faqSchema, jsonLd, seo } from '../../lib/seo.js';
import { startUrl } from '../../lib/site.js';

/**
 * One kind of business, on its own page.
 *
 * ===========================================================================
 * Ten pages from one route, which is the shape that usually goes wrong
 * ===========================================================================
 * A route per industry is how thin programmatic content gets made: ten pages
 * whose only difference is a substituted noun, each ranking for nothing and
 * insulting the trade it names. The site already rules that out for the widget
 * pages, and the rule is the same here — every field this route renders is
 * written per industry in `industries.ts`, and `industries.test.ts` fails if two
 * pages share a headline, an intro, a bullet or an answer.
 *
 * What earns each page is that the questions genuinely differ. A dentist asks
 * whether their regulator allows patient testimonials at all. An agency asks
 * whether there is white labelling. An Etsy seller asks why their marketplace
 * needs a browser extension when Google does not. Those are three unrelated
 * conversations, and a single page addressing "businesses" has none of them.
 *
 * ===========================================================================
 * The page ends by sending people away
 * ===========================================================================
 * Twice, deliberately: to the source page for the platform their reviews are
 * actually on, and to the layout that suits their kind of site. This page is
 * the door, and the rooms behind it are already built and already maintained —
 * duplicating "how the Google import works" into ten trade pages would be ten
 * copies to keep true.
 */
export const Route = createFileRoute('/who-its-for/$slug')({
  loader: ({ params }) => {
    const industry = industryById(params.slug);
    if (!industry) throw notFound();
    return { industry };
  },
  head: ({ loaderData }) => {
    const industry = loaderData?.industry;
    if (!industry) return {};

    const path = `/who-its-for/${industry.id}`;
    const { meta, links } = seo({
      title: industry.seoTitle,
      description: industry.seoDescription,
      path,
    });

    return {
      meta,
      links,
      scripts: [
        {
          type: 'application/ld+json',
          children: jsonLd(
            breadcrumbSchema([
              { name: 'Home', path: '/' },
              { name: 'Who it is for', path: '/who-its-for' },
              { name: industry.name, path },
            ])
          ),
        },
        /*
          The FAQ block, because these are the questions the trade actually
          types — "can a patient ask for their review to be taken down", "do I
          need an account per client" — and a rich result that answers one in
          the search page is worth more than a click that has to hunt for it.
        */
        {
          type: 'application/ld+json',
          children: jsonLd(faqSchema(industry.faqs)),
        },
      ],
    };
  },
  component: IndustryPage,
});

function IndustryPage() {
  const { industry } = Route.useLoaderData();
  const widget = widgetContentByType(industry.widget);
  const surface = `industry-${industry.id}`;

  /*
    The sample set for the platform these people's reviews are actually on, so
    the widget further down is drawn with reviews that sound like their
    customers. A hotel page showing reviews of a plumber would undo the whole
    argument of the page above it.
  */
  const reviews = sampleSetForSource(industry.sources[0]!).reviews.slice(0, 6);

  return (
    <>
      <PageHero
        trail={[
          { name: 'Home', path: '/' },
          { name: 'Who it is for', path: '/who-its-for' },
          { name: industry.name, path: `/who-its-for/${industry.id}` },
        ]}
        art={<IndustryScene sources={industry.sources} />}
      >
        <Measure>
          <Eyebrow>{industry.name}</Eyebrow>
          <Heading level={1} size="hero" className="mt-4">
            <MarkPhrase text={industry.headline} phrase={industry.markPhrase} />
          </Heading>
          <Text size="lead" className="mt-6">
            {industry.intro}
          </Text>

          <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center">
            <ButtonAnchor
              href={startUrl(surface)}
              size="lg"
              onClick={() => track('hero_cta_clicked', { surface })}
            >
              Start free
            </ButtonAnchor>
            <ButtonLink
              to={widget ? widgetPath(widget) : '/widgets'}
              variant="secondary"
              size="lg"
            >
              See the {widget?.seoName.toLowerCase() ?? 'widget'} layout
            </ButtonLink>
          </div>

          {/*
            The platforms these people's reviews are already on, in the hero
            rather than further down. It is the fact that tells somebody in ten
            seconds whether this page is about them, and each one links to the
            page that explains that import properly.
          */}
          <div className="mt-9 flex flex-wrap items-center gap-x-3 gap-y-2">
            <span className="text-caption text-ink-500">
              Your reviews are probably on
            </span>
            {industry.sources.map((source) => (
              <SiteLink
                key={source}
                to={sourcePagePath(source)}
                className="inline-flex items-center gap-2 rounded-lg bg-ink-25 px-2.5 py-1.5 text-caption font-medium text-ink-700 ring-1 ring-ink-150 transition-colors duration-[var(--duration-fast)] hover:bg-white hover:text-ink-950 hover:ring-ink-300"
              >
                <SourceGlyph source={source} className="size-3.5 shrink-0" />
                {REVIEW_SOURCE_DEFINITIONS[source].label}
              </SiteLink>
            ))}
          </div>
        </Measure>
      </PageHero>

      {/* ------------------------------------------------- The situation */}
      <Section aria-labelledby="problem-heading">
        <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:gap-16">
          <div>
            <Heading level={2} id="problem-heading">
              Where your reviews are now.
            </Heading>
            <Text className="mt-4 max-w-prose">{industry.problem}</Text>
          </div>
          <div className="lg:pt-14">
            <div className="border-l-2 border-accent-400 pl-6">
              <p className="eyebrow text-ink-500">And afterwards</p>
              <p className="mt-2 max-w-prose text-body-lg text-ink-900">
                {industry.outcome}
              </p>
            </div>
          </div>
        </div>
      </Section>

      {/* ------------------------------------------------ Why it fits you */}
      <Section tone="raised" aria-labelledby="points-heading">
        <Measure>
          <Heading level={2} id="points-heading">
            Why it fits {industry.name}.
          </Heading>
        </Measure>

        <div className="mt-10 grid gap-8 md:grid-cols-3">
          {industry.points.map((point) => (
            <div key={point.title}>
              <p className="text-title text-ink-950">{point.title}</p>
              <p className="mt-2 max-w-prose text-body-sm text-ink-600">
                {point.body}
              </p>
            </div>
          ))}
        </div>
      </Section>

      {/* --------------------------------------------- What it looks like */}
      <Section aria-labelledby="preview-heading">
        <Measure>
          <Heading level={2} id="preview-heading">
            What it looks like on your site.
          </Heading>
          <Text className="mt-4 max-w-prose">
            {widget ? (
              <>
                The {widget.seoName.toLowerCase()} layout, drawn by the same
                renderer that will run on your website. {industry.widgetReason}
              </>
            ) : (
              industry.widgetReason
            )}
          </Text>
        </Measure>

        <div className="rj-canvas mt-10 rounded-2xl p-4 ring-1 ring-ink-150 sm:p-8">
          <WidgetShowcase type={industry.widget} reviews={reviews} />
        </div>

        <p className="mt-3 text-caption text-ink-500">
          Sample reviews written for {industry.name}. Yours replace them once
          you connect a source.
        </p>

        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
          <ButtonLink to="/tools/review-widget-generator" size="lg">
            Build one free
          </ButtonLink>
          <ButtonLink to="/widgets" variant="secondary" size="lg">
            See every layout
          </ButtonLink>
        </div>
      </Section>

      {/* -------------------------------------------------------- The FAQ */}
      <Section tone="raised" aria-labelledby="faq-heading">
        <Measure>
          <Heading level={2} id="faq-heading">
            Questions from {industry.name}.
          </Heading>
        </Measure>
        <dl className="mt-10 max-w-text">
          {industry.faqs.map((faq, index) => (
            <div
              key={faq.q}
              className={index > 0 ? 'mt-8 border-t border-ink-150 pt-8' : ''}
            >
              <dt className="text-title text-ink-950">{faq.q}</dt>
              <dd className="mt-2.5 text-body text-ink-600">{faq.a}</dd>
            </div>
          ))}
        </dl>
      </Section>

      {/* ------------------------------------------------- Other trades */}
      {/*
        Internal linking, and a genuine escape hatch. Somebody who followed a
        search here and finds it is not quite their trade should reach the
        nearest one in a click rather than going back to the results.
      */}
      <Section aria-labelledby="others-heading">
        <Measure>
          <Heading level={2} id="others-heading" size="title">
            Not quite your line of work?
          </Heading>
          <Text className="mt-4 max-w-prose">
            The same product, described for the business doing the reading.
          </Text>
        </Measure>

        <ul className="mt-8 flex flex-wrap gap-2">
          {INDUSTRIES.filter((entry) => entry.id !== industry.id).map((entry) => (
            <li key={entry.id}>
              <SiteLink
                to={`/who-its-for/${entry.id}`}
                className="inline-block rounded-lg px-3 py-1.5 text-body-sm font-medium text-ink-700 ring-1 ring-ink-200 transition-colors duration-[var(--duration-fast)] hover:bg-ink-25 hover:text-ink-950 hover:ring-ink-300"
              >
                {entry.name}
              </SiteLink>
            </li>
          ))}
        </ul>
      </Section>

      <Section tone="ink" size="base">
        <Measure>
          <Heading level={2} className="text-white">
            Your reviews are already written.
          </Heading>
          <Text className="mt-4 text-white/70">
            Connect the listing they are on, choose the ones worth showing, and
            paste one line of code. Free plan, no card.
          </Text>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
            <ButtonAnchor
              href={startUrl(`${surface}-close`)}
              variant="inverse"
              size="lg"
              onClick={() =>
                track('hero_cta_clicked', { surface: `${surface}-close` })
              }
            >
              Start free
            </ButtonAnchor>
            <ButtonLink to="/pricing" variant="inverse-ghost" size="lg">
              See pricing
            </ButtonLink>
          </div>
        </Measure>
      </Section>
    </>
  );
}
